import { createAsyncThunk } from '@reduxjs/toolkit'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import {
  getProductType,
  GetProductTypeResponseType,
  getInventoryStatusNotes,
} from 'features/Product/services/product'
import {
  getSOBusinessTaggingConfigAPI,
  GetSOBusinessTaggingConfigResponseType,
  getSOProductsDirectAPI,
  GetSOProductsDirectResponseType,
  postSupplyOrderDetailAPI,
  PostSupplyOrderDetailRequestType,
  putSupplyOrderDetailAPI,
  PutSupplyOrderDetailRequestType,
} from 'features/SupplyOrder/services/outboundSO'
import { callErrorMsg } from 'helpers/errorMsg'

export const SLICE_NAME = 'OUTBOUND_SO_ADD_PRODUCT'

export const fetchGetSOBusinessTaggingCongig = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSOBusinessTaggingCongig`,
  async (
    props: {
      destinationId: number
      onSuccess?: (data: GetSOBusinessTaggingConfigResponseType['data']) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { destinationId, onSuccess } = props

      const res = await getSOBusinessTaggingConfigAPI(destinationId)

      onSuccess?.(res.data.data)

      return res.data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSOProductDirect = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSOProductDirect`,
  async (params: Parameters<typeof getSOProductsDirectAPI>[0], { rejectWithValue, getState }) => {
    try {
      const {
        outboundSOAddProductDirect: { query },
      } = getState() as StoreStateType

      const res = await getSOProductsDirectAPI({ ...query, ...params })

      const result: GetSOProductsDirectResponseType = {
        data: res.data.data,
      }

      return result
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPutSupplyOrderDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchPutSupplyOrderDetail`,
  async (
    props: {
      supplyOrderId: number
      payload: PutSupplyOrderDetailRequestType
      onSuccess?: () => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { supplyOrderId, payload, onSuccess } = props
      const response = await putSupplyOrderDetailAPI(supplyOrderId, payload)

      onSuccess?.()

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPostSupplyOrderDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchPostSupplyOrderDetail`,
  async (
    props: {
      payload: PostSupplyOrderDetailRequestType
      onSuccess?: (supplyOrderId: number) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { payload, onSuccess } = props
      const response = await postSupplyOrderDetailAPI(payload)

      onSuccess?.(response.data.data.supplyOrderId)

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetProductType = createAsyncThunk(
  `${SLICE_NAME}/getProductType`,
  async (
    props: {
      destinationId: number
      onSuccess?: (data: GetProductTypeResponseType['data']) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { destinationId, onSuccess } = props || {}

      const response = await getProductType()

      const WAREHOUSE_ID = {
        cbt: 819,
        pgs: 160,
        cbn: 661,
        stl: 772,
        sunterOverflow: 912,
        bgo: 860, // bogor
      }

      const listProductType = [
        'dry',
        'fresh',
        'freebies',
        'raw material',
        'supplies',
        'gift customer',
        'pwp',
        'packaging',
        'general merchandise',
        'merchandise astro club',
        'astro kitchen - supplies',
        'astro kitchen - fixed assets',
        'astro kitchen - finished goods',
        'astro kitchen - raw material chilled/frozen',
        'astro kitchen - raw material dry',
        'astro kitchen - raw material packaging',
        'astro kitchen - packaging outer',
        'frozen',
        'others - inventories',
      ]

      let modifiedProductTypeList = response.data.data.filter((itemData) =>
        listProductType.includes(itemData.product_type_name.toLowerCase()),
      )

      let productTypeToExclude: string[] = []
      // CBT & STL -> Dry
      // PGS & SUNTER OVERFLOW -> Frozen
      // CBN & BOGOR -> Fresh
      if ([WAREHOUSE_ID.cbt, WAREHOUSE_ID.stl].includes(destinationId))
        productTypeToExclude = ['fresh', 'frozen']
      else if ([WAREHOUSE_ID.pgs, WAREHOUSE_ID.sunterOverflow].includes(destinationId))
        productTypeToExclude = ['fresh', 'dry']
      else if ([WAREHOUSE_ID.cbn, WAREHOUSE_ID.bgo].includes(destinationId))
        productTypeToExclude = ['dry', 'frozen']

      if (
        [
          WAREHOUSE_ID.cbt,
          WAREHOUSE_ID.stl,
          WAREHOUSE_ID.pgs,
          WAREHOUSE_ID.sunterOverflow,
          WAREHOUSE_ID.cbn,
          WAREHOUSE_ID.bgo,
        ].includes(destinationId)
      ) {
        modifiedProductTypeList = modifiedProductTypeList.filter(
          (item) => !productTypeToExclude.includes(item.product_type_name.toLowerCase()),
        )
      }

      onSuccess?.(modifiedProductTypeList)

      return modifiedProductTypeList
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetInventoryStatusNotes = createAsyncThunk(
  `${SLICE_NAME}/fetchGetInventoryStatusNotes`,
  async (statusId: number, { rejectWithValue }) => {
    try {
      const {
        data: { data },
      } = await getInventoryStatusNotes({
        page_size: 100,
        page_index: 1,
        inventory_status_id: statusId,
        exclusion_preset: 0,
      })
      return data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)
